import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  ListTodo, 
  Plus, 
  Check, 
  Trash2, 
  Calendar,
  Flag,
  Edit2,
  X,
  CheckCircle2,
  Circle,
  Clock
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const PRIORITY_COLORS = {
  high: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  medium: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  low: 'border-green-500 bg-green-50 dark:bg-green-900/20'
};

const PRIORITY_BADGES = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
  low: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
};

const ArtistTasks = () => {
  const { isDark } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      if (editingTask) {
        await api.put(`/artist/tasks/${editingTask.id}`, formData);
        toast.success('Task updated');
      } else {
        await api.post('/artist/tasks', formData);
        toast.success('Task created');
      }
      resetForm();
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save task');
    }
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await api.put(`/artist/tasks/${task.id}`, { ...task, status: newStatus });
      toast.success(newStatus === 'completed' ? 'Task completed! 🎉' : 'Task reopened');
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await api.delete(`/artist/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      due_date: task.due_date || '',
      status: task.status
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      status: 'pending'
    });
    setEditingTask(null);
    setShowForm(false);
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'pending') return task.status !== 'completed';
    if (filter === 'completed') return task.status === 'completed';
    return task.priority === filter;
  });

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status !== 'completed').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => t.status !== 'completed' && t.due_date && new Date(t.due_date) < new Date()).length
  };

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-tasks">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className={cn("text-2xl font-bold font-outfit flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <ListTodo className="w-7 h-7 text-[#331062]" />
              My Tasks
            </h1>
            <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
              Manage your personal to-do list and stay organized
            </p>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="bg-[#331062] hover:bg-[#2A0D52]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.total}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Total Tasks</p>
          </Card>
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className={cn("text-2xl font-bold text-blue-600")}>{stats.pending}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Pending</p>
          </Card>
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className={cn("text-2xl font-bold text-green-600")}>{stats.completed}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Completed</p>
          </Card>
          <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <p className={cn("text-2xl font-bold text-red-600")}>{stats.overdue}</p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Overdue</p>
          </Card>
        </div>

        {/* Add/Edit Task Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                    {editingTask ? 'Edit Task' : 'New Task'}
                  </h3>
                  <button onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Task Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="What do you need to do?"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Add details..."
                      rows={2}
                      className={cn(
                        "w-full mt-1 px-3 py-2 rounded-md border text-sm resize-none",
                        isDark 
                          ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400" 
                          : "bg-white border-slate-300"
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Priority</Label>
                      <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">🔴 High</SelectItem>
                          <SelectItem value="medium">🟡 Medium</SelectItem>
                          <SelectItem value="low">🟢 Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="due_date">Due Date</Label>
                      <Input
                        id="due_date"
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-[#331062] hover:bg-[#2A0D52]">
                      {editingTask ? 'Update Task' : 'Create Task'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['all', 'pending', 'completed', 'high', 'medium', 'low'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={cn(filter === f && "bg-[#331062]")}
            >
              {f === 'all' && 'All'}
              {f === 'pending' && '⏳ Pending'}
              {f === 'completed' && '✅ Completed'}
              {f === 'high' && '🔴 High'}
              {f === 'medium' && '🟡 Medium'}
              {f === 'low' && '🟢 Low'}
            </Button>
          ))}
        </div>

        {/* Tasks List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <Card className={cn("p-12 text-center", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <ListTodo className={cn("w-16 h-16 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
            <h3 className={cn("text-lg font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
              {filter === 'all' ? 'No tasks yet' : `No ${filter} tasks`}
            </h3>
            <p className={cn("text-sm mb-4", isDark ? "text-slate-400" : "text-slate-500")}>
              Start by creating your first task to stay organized
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-[#331062] hover:bg-[#2A0D52]">
              <Plus className="w-4 h-4 mr-2" /> Add Task
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {filteredTasks.map((task) => {
                const isOverdue = task.status !== 'completed' && task.due_date && new Date(task.due_date) < new Date();
                
                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    layout
                  >
                    <Card className={cn(
                      "p-4 border-l-4 transition-all",
                      task.status === 'completed' 
                        ? "border-slate-300 bg-slate-50 dark:bg-slate-800/50 dark:border-slate-600 opacity-70" 
                        : PRIORITY_COLORS[task.priority],
                      isDark && task.status !== 'completed' && "border-slate-700"
                    )}>
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <button
                          onClick={() => handleToggleStatus(task)}
                          className="mt-1 flex-shrink-0"
                        >
                          {task.status === 'completed' ? (
                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                          ) : (
                            <Circle className={cn(
                              "w-6 h-6",
                              isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"
                            )} />
                          )}
                        </button>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={cn(
                              "font-medium",
                              task.status === 'completed' && "line-through",
                              isDark ? "text-white" : "text-slate-800"
                            )}>
                              {task.title}
                            </h4>
                            <span className={cn("text-xs px-2 py-0.5 rounded-full capitalize", PRIORITY_BADGES[task.priority])}>
                              {task.priority}
                            </span>
                            {isOverdue && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                                Overdue
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className={cn(
                              "text-sm mt-1",
                              task.status === 'completed' && "line-through",
                              isDark ? "text-slate-400" : "text-slate-500"
                            )}>
                              {task.description}
                            </p>
                          )}
                          {task.due_date && (
                            <p className={cn(
                              "text-xs mt-2 flex items-center gap-1",
                              isOverdue ? "text-red-600" : (isDark ? "text-slate-500" : "text-slate-400")
                            )}>
                              <Calendar className="w-3 h-3" />
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(task)}
                            className="h-8 w-8"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(task.id)}
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </ArtistLayout>
  );
};

export default ArtistTasks;
